"use client";
import {
  CardMedia,
  Typography,
  Box,
  IconButton,
  Avatar,
  Stack,
  Chip,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { PlayArrow } from "@mui/icons-material";

import image_for_coffe_call from "../../assets/Upstream.png";
import { ChatBubbleIcon, LikeIcon } from "../../assets/reactIcons";
import { Icons } from "../../assets/icon";

interface PostCardProps {
  mediaType?: string;
  mediaUrl?: string;
  title: string;
  description: string;
  likes?: number;
  comments?: number;
  shares?: number;
  timestamp?: string;
  onLike?: () => void;
  onComment?: () => void;
  onShare?: () => void;
  data: {
    name: string;
    username: string;
    avatar: string;
  };
  imageHeight?: string;
}

const TalentPostCard = ({
  mediaType = "none",
  mediaUrl = "",
  title,
  description,
  likes = 0,
  comments = 0,
  shares = 0,
  timestamp = "",
  onLike,
  onComment,
  onShare,
  data,
  imageHeight = "300px",
}: PostCardProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Stack
      sx={{
        padding: isMobile ? 1.5 : 2,
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? "12px" : "16px",
      }}
    >
      <Stack direction={isMobile ? "column" : "row"} spacing={isMobile ? 1 : 2}>
        <Avatar
          src={image_for_coffe_call}
          alt={data?.username}
          sx={{
            width: isMobile ? 32 : 40,
            height: isMobile ? 32 : 40,
            borderRadius: "4px",
          }}
        />
        <Box>
          <Stack direction={isMobile ? "column" : "row"} spacing={1}>
            <Typography
              sx={{
                fontSize: isMobile ? "16px" : "18px",
                lineHeight: isMobile ? "20px" : "24px",
                fontWeight: 500,
                color: "var(--neutralBlack)",
                mb: isMobile ? 0 : 0.5,
              }}
            >
              {data.name}
            </Typography>
            <Chip
              label="Talent"
              sx={{
                backgroundColor: "var(--primary-600)",
                color: "white",
                fontSize: isMobile ? "12px" : "14px",
                fontWeight: 500,
                borderRadius: "12px",
                height: isMobile ? "18px" : "20px",
                display: "flex",
                alignItems: "center",
                width: "fit-content",
              }}
            />
          </Stack>
          <Typography
            sx={{
              fontSize: isMobile ? "11px" : "12px",
              lineHeight: isMobile ? "14px" : "16px",
              color: "var(--neutral-500)",
            }}
          >
            {timestamp}
          </Typography>
        </Box>
      </Stack>

      <Typography
        sx={{
          fontSize: isMobile ? "13px" : "14px",
          fontWeight: 400,
          color: "var(--neutral-700)",
          lineHeight: isMobile ? "18px" : "20px",
        }}
      >
        {description}
      </Typography>

      {mediaType !== "none" && (
        <Box sx={{ position: "relative" }}>
          {mediaType === "video" ? (
            <video
              controls
              src={mediaUrl}
              title={title}
              style={{
                width: "100%",
                borderRadius: "4px",
                height: isMobile ? "200px" : imageHeight,
              }}
            />
          ) : (
            <CardMedia
              component="img"
              image={mediaUrl}
              title={title}
              sx={{
                borderRadius: "4px",
                height: isMobile ? "200px" : imageHeight,
                objectFit: "cover",
              }}
            />
          )}
          {mediaType === "video" && (
            <IconButton
              aria-label="play"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                },
                width: isMobile ? 40 : 48,
                height: isMobile ? 40 : 48,
              }}
            >
              <PlayArrow sx={{ fontSize: isMobile ? 40 : 60 }} />
            </IconButton>
          )}
        </Box>
      )}

      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={isMobile ? 1 : 2}
        justifyContent="space-between"
        alignItems={isMobile ? "flex-start" : "center"}
      >
        <Box>
          <Typography variant="body2" color="textSecondary" sx={{ fontSize: isMobile ? "12px" : "14px" }}>
            {likes} likes
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: isMobile ? "12px" : "14px" }}>
          {comments} Comments • {shares} Shares
        </Typography>
      </Stack>
      <Stack direction="row" spacing={isMobile ? 0.5 : 0}>
        <Button
          sx={{
            flex: 1,
            borderRadius: isMobile ? "8px" : "8px 0 0 8px",
            border: "1px solid var(--neutral-300)",
            padding: isMobile ? "4px" : "8px",
          }}
          variant="outlined"
          color="primary"
          onClick={onLike}
        >
          <IconButton aria-label="like" sx={{ padding: isMobile ? "4px" : "8px" }}>
            <LikeIcon style={{ color: "black", fontSize: isMobile ? 16 : 20 }} />
          </IconButton>
          <Typography
            sx={{
              fontSize: isMobile ? "12px" : "14px",
              lineHeight: isMobile ? "14px" : "16px",
              color: "var(--neutral-1000)",
            }}
            variant="body2"
          >
            Like
          </Typography>
        </Button>
        <Button
          sx={{
            flex: 1,
            borderRadius: isMobile ? "8px" : "0",
            border: "1px solid var(--neutral-300)",
            padding: isMobile ? "4px" : "8px",
          }}
          variant="outlined"
          onClick={onComment}
        >
          <IconButton aria-label="comment" sx={{ padding: isMobile ? "4px" : "8px" }}>
            <ChatBubbleIcon style={{ fontSize: isMobile ? 16 : 20, color: "black" }} />
          </IconButton>
          <Typography
            sx={{
              fontSize: isMobile ? "12px" : "14px",
              lineHeight: isMobile ? "14px" : "16px",
              color: "var(--neutral-1000)",
            }}
            variant="body2"
          >
            Comment
          </Typography>
        </Button>
        <Button
          sx={{
            flex: 1,
            borderRadius: isMobile ? "8px" : "0 8px 8px 0",
            border: "1px solid var(--neutral-300)",
            padding: isMobile ? "4px" : "8px",
          }}
          variant="outlined"
          color="primary"
          onClick={onShare}
        >
          <IconButton aria-label="share" sx={{ padding: isMobile ? "4px" : "8px" }}>
            <Icons.ShareIcon style={{ color: "black", fontSize: isMobile ? 16 : 20 }} />
          </IconButton>
          <Typography
            sx={{
              fontSize: isMobile ? "12px" : "14px",
              lineHeight: isMobile ? "14px" : "16px",
              color: "var(--neutral-1000)",
            }}
            variant="body2"
            color="textSecondary"
          >
            Share
          </Typography>
        </Button>
      </Stack>
    </Stack>
  );
};

export default TalentPostCard;
